// janus-engine/actions/self-status.ts — A1 (Dodge): the first client-truth USE-ABILITY slice.
//
// PURE. Resolves ONE self-target, no-roll ability whose own row becomes the lingering status
// (item-as-status; the CLAUDE.md rule) against the caster row + encounter blob the browser already
// holds, and produces the commit envelope (`janus.action.truth.v1`) plus the frame every browser
// applies to its row, including the browser-decided status clock and expiry.
// Three outcomes:
//   supported  — envelope + frame; the caller mutates the row, renders, and commits (publish-first).
//   refused    — the legacy path would refuse with this exact message; nothing is sent anywhere.
//   unsupported — outside A1's boundary; the caller can try another native lane.
// Owner rulings (2026-09-06): in-combat is PER CREATURE (its combat_id against the encounter's open
// combat); out of combat the status applies with no AP charge; a re-use while active burns the AP in
// combat and applies nothing (legacy parity); the skills cluster is patched, not recompiled.

import { encounterCreatureField } from '../../shared/array-types.js';
import { applyApsScalar } from '../movement/client-truth-commit.js';
import { statusPreventsActions } from './status-action-gate.js';
import { planSelfStatusSave, type SelfStatusClockReaders } from './self-status-save.js';

type Row = Record<string, unknown>;

export const SELF_STATUS_SCHEMA = 'janus.action.truth.v1' as const;

/** The ONLY layers a supported definition may carry (Dodge's exact set, S1 golden). Anything else
 * is another native slice's business. */
const SUPPORTED_DEFINITION_LAYERS = new Set([
  'range', 'skill_roll_adv', 'effect_ends_on_start_of_caster_next_turn',
  'if_caster_has_status_effect_not_active', 'roll_type_against_dis',
]);
const RANGE_SELF = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';
const ABILITY_AP_COST: Record<string, number> = { ability: 2 };

export interface SelfStatusReaders extends SelfStatusClockReaders {
  /** ftLayerValue twin. */
  value: (row: Row, name: string) => unknown;
  /** ftLayerEntries twin. */
  entries: (row: Row, name: string) => unknown[];
  /** Full definition {items, item_layer_types} for a catalog id (row catalog → object cache). */
  definition: (id: string) => Row | null;
  /** Resolved TARGET-side status rows of a carrier (ftCreatureStatuses(row, 'target')). */
  statuses: (row: Row) => Row[];
  /** Display name for a catalog id (refusal text parity). */
  name: (id: string) => string;
  /** Per-creature combat membership (creatureIsInActiveCombat twin). */
  inCombat: (encounter: Row, row: Row) => boolean;
  /** computed.creature.skills bucket KEY for a skill item id (SKILL_CATALOG). */
  skillVariable: (skillId: string) => string | null;
  /** VISUAL_STATUS_EFFECTS-keyed status presence (movement's `status()` twin), e.g. 'hidden'. */
  hasStatus: (row: Row, name: string) => boolean;
  /** Fresh uppercase UUID (status_id / status_connected_id / operation_id). */
  uuid: () => string;
}

export interface SelfStatusInput {
  encounterId: string;
  actorId: string;
  abilityId: string;
  userId: string;
  isGM: boolean;
  /** The sidelog privacy choice (0 public / 1 private / 2 secret) the log rows inherit. */
  rollPrivate?: number;
}

/** The browser's lean projection of the exact native saved row. */
export interface LeanStatusEntry {
  status_id: string;
  status_target_id: string;
  status_stack: number;
  status_connected_id: string;
  status_campaign_id?: string;
  status_ends_on_caster_start_of_next_turn?: number;
  status_item_id: string;
  [key: string]: unknown;
}

export interface SkillAdvPatch {
  adv_dis_stacks: number;
  adv_dis_breakdown: { lines: Array<Record<string, unknown>> };
}

export interface ActionTruthFrame {
  /** The applied status (null on a re-use that only burns AP). */
  statuses_target_entry: LeanStatusEntry | null;
  aps: number;
  aps_used: number | null;
  /** Whole-entry replacements for computed.creature.skills.<var> (server patches the same). */
  skills: Record<string, SkillAdvPatch>;
}

export interface ActionTruthCommand {
  schema: typeof SELF_STATUS_SCHEMA;
  operation_id: string;
  encounter_id: string;
  actor_id: string;
  expected_rev: number;
  ability_id: string;
  roll_private: number;
  before: { aps: number };
  writes: {
    encounter_creatures: { aps?: number };
    statuses_insert: Array<Record<string, unknown>>;
    log: { key: 'ability_applied' | 'ability_noop'; ability_id: string };
  };
  frame: ActionTruthFrame;
  rev?: number;
}

export type SelfStatusScope =
  | { supported: true; command: ActionTruthCommand }
  | { supported: false; refused: true; message: string }
  | { supported: false; refused: false; reason: string };

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const uuidRe = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const present = (value: unknown): boolean => value !== undefined && value !== null && value !== '';

function itemField(row: Row | null, key: string): unknown {
  const items = record(row?.items);
  return scalar(items?.[key] ?? row?.[key]);
}
function layerIds(entries: unknown[]): string[] {
  const out: string[] = [];
  for (const entry of entries) {
    const id = typeof entry === 'string' ? entry : upper(itemField(record(entry), 'item_id') ?? record(entry)?.ability_id);
    if (uuidRe.test(upper(id))) out.push(upper(id));
  }
  return out;
}
/** has_status_effect twin over the RESOLVED target statuses. */
function statusItemIds(read: SelfStatusReaders, row: Row): Set<string> {
  const ids = new Set<string>();
  for (const status of read.statuses(row)) {
    const def = record(status.status_arr);
    const id = upper(status.status_item_id ?? itemField(def, 'item_id') ?? itemField(status, 'item_id'));
    if (id) ids.add(id);
  }
  return ids;
}

export function resolveSelfStatusAction(input: SelfStatusInput, encounter: Row, caster: Row, read: SelfStatusReaders): SelfStatusScope {
  const no = (reason: string): SelfStatusScope => ({ supported: false, refused: false, reason });
  const refuse = (message: string): SelfStatusScope => ({ supported: false, refused: true, message });

  const abilityId = upper(input.abilityId);
  const ec = record(caster.encounter_creatures);
  if (!ec || upper(ec.encounter_creature_id) !== upper(input.actorId)) return no('actor-row');
  if (upper(ec.encounter_id) !== upper(input.encounterId)) return no('actor-encounter');
  const ownerId = upper(ec.encounter_creature_created_by_id);
  if (ownerId !== upper(input.userId) && !input.isGM) return no('permission');
  if (upper(itemField(caster, 'item_type')) === 'LAIR') return no('lair');
  if (present(read.value(caster, 'legendary_creature'))) return no('legendary');
  const casterLayers = record(caster.item_layer_types) ?? {};
  if (Object.keys(casterLayers).some(key => key.startsWith('triggers_when_'))) return no('caster-triggers');

  // ---- definition boundary (Dodge's exact shape) ----
  const definition = read.definition(abilityId);
  if (!definition) return no('definition-missing');
  if (upper(itemField(definition, 'item_type')) !== 'ABILITY') return no('item-type');
  const layers = record(definition.item_layer_types) ?? {};
  for (const key of Object.keys(layers)) if (!SUPPORTED_DEFINITION_LAYERS.has(key)) return no('layer:' + key);
  if (upper(read.value(definition, 'range')) !== RANGE_SELF) return no('range');
  if (!present(read.value(definition, 'effect_ends_on_start_of_caster_next_turn'))) return no('duration');
  if (present(read.value(definition, 'roll_type'))) return no('roll');
  const apCost = ABILITY_AP_COST[String(itemField(definition, 'item_type')).toLowerCase()] ?? null;
  if (apCost === null) return no('ap-cost');

  // ---- gates in the legacy order: turn/AP (check_caster_aps_and_in_combat) → gating statuses ----
  const inCombat = read.inCombat(encounter, caster);
  const aps = Number(encounterCreatureField(ec, 'aps') ?? 0);
  if (!Number.isInteger(aps)) return no('aps-shape');
  const casterStatusIds = statusItemIds(read, caster);
  if (statusPreventsActions(caster, read)) return refuse('You cannot take actions while an active status prevents actions.');
  if (inCombat) {
    const turnId = upper(read.value(encounter, 'current_encounter_creature_id_turn'));
    if (turnId !== upper(input.actorId) && !input.isGM) {
      return refuse('While in combat, you can only use abilities that use reactions or triggers when it is not your turn.');
    }
    if (aps < apCost) return refuse(`You do not have enough Action Points. This ability requires ${apCost} AP, you have ${aps}.`);
  }
  for (const gateId of layerIds(read.entries(definition, 'if_caster_has_status_effect_not_active'))) {
    if (casterStatusIds.has(gateId)) return refuse(`You currently have the status effect ${read.name(gateId)} active and CANNOT use this ability!`);
  }
  // Stealth: acting from Hidden is a reveal decision the legacy predicate owns; keep it there.
  if (read.hasStatus(caster, 'hidden')) return no('hidden');

  // ---- outcome ----
  const alreadyActive = casterStatusIds.has(abilityId); // apply_status_effect dup guard: AP spent, nothing applied
  const apsAfter = inCombat ? aps - apCost : aps;
  const apsTotal = Number(encounterCreatureField(ec, 'aps_total') ?? 3);
  const apsUsed = Math.max(0, apsTotal - apsAfter);
  const frame: ActionTruthFrame = { statuses_target_entry: null, aps: apsAfter, aps_used: apsUsed > 0 ? apsUsed : null, skills: {} };
  const writes: ActionTruthCommand['writes'] = {
    encounter_creatures: inCombat ? { aps: apsAfter } : {},
    statuses_insert: [],
    log: { key: alreadyActive ? 'ability_noop' : 'ability_applied', ability_id: abilityId },
  };

  if (!alreadyActive) {
    const statusId = read.uuid();
    const connectedId = read.uuid();
    const status = planSelfStatusSave({ statusId, itemId: abilityId, actorId: upper(input.actorId), connectedId }, caster, definition, read);
    frame.statuses_target_entry = status.entry as unknown as LeanStatusEntry;
    writes.statuses_insert.push(status.row);
    // computed.creature.skills — compute_caster_skill_adv_dis: the statuses walk runs LAST, so the
    // new line is appended; net = sum of lines; lean lines carry {value, href_id:<status_id>} only.
    const skills = record(record(record(caster.computed)?.creature)?.skills);
    for (const skillId of layerIds(read.entries(definition, 'skill_roll_adv'))) {
      const variable = read.skillVariable(skillId);
      if (!variable) return no('skill-variable:' + skillId);
      const existing = record(skills?.[variable]);
      if (!existing) return no('skill-entry-elided:' + variable); // PHP would mint a default entry; not this slice
      const breakdown = record(existing.adv_dis_breakdown);
      const lines = Array.isArray(breakdown?.lines) ? (breakdown!.lines as Array<Record<string, unknown>>).map(line => ({ ...line })) : [];
      lines.push({ value: 1, href_id: statusId });
      const stacks = lines.reduce((sum, line) => sum + Number(line.value ?? 0), 0);
      frame.skills[variable] = { adv_dis_stacks: stacks, adv_dis_breakdown: { lines } };
    }
  }

  return {
    supported: true,
    command: {
      schema: SELF_STATUS_SCHEMA,
      operation_id: read.uuid(),
      encounter_id: upper(input.encounterId),
      actor_id: upper(input.actorId),
      expected_rev: Number(encounterCreatureField(ec, 'movement_rev') ?? 0),
      ability_id: abilityId,
      roll_private: [0, 1, 2].includes(Number(input.rollPrivate)) ? Number(input.rollPrivate) : 0,
      before: { aps },
      writes,
      frame,
    },
  };
}

/** Mutate the caster row IN PLACE with a frame (optimistic or received). Keeps row and bucket
 * identity: the encounter store and caster cache share them. */
export function applyActionTruth(row: Row, frame: ActionTruthFrame, rev?: number, definition?: Row | null): void {
  const ec = (row.encounter_creatures ??= {}) as Row;
  applyApsScalar(ec, frame.aps, frame.aps_used);
  if (Number.isInteger(rev) && rev! >= 0) ec.movement_rev = rev;
  const entry = frame.statuses_target_entry;
  if (entry) {
    const statuses = (row.statuses ??= {}) as Row;
    for (const mode of ['target', 'caster'] as const) {
      const list = Array.isArray(statuses[mode]) ? statuses[mode] as Row[] : (statuses[mode] = []) as Row[];
      const index = list.findIndex(existing => upper(existing.status_id) === upper(entry.status_id));
      if (index >= 0) list[index] = { ...list[index], ...entry };
      else list.unshift({ ...entry }); // a NEW status first (PHP lists most-recently-touched first — N4e)
    }
    if (definition) {
      const catalog = record(row.catalog) ?? {};
      const key = upper(entry.status_item_id);
      if (!record(catalog[key])?.item_layer_types) row.catalog = { ...catalog, [key]: definition };
    }
  }
  const computed = (row.computed ??= {}) as Row;
  const creature = (computed.creature ??= {}) as Row;
  const skills = (creature.skills ??= {}) as Row;
  for (const [variable, patch] of Object.entries(frame.skills ?? {})) {
    const existing = record(skills[variable]) ?? {};
    skills[variable] = { ...existing, adv_dis_stacks: patch.adv_dis_stacks, adv_dis_breakdown: { lines: patch.adv_dis_breakdown.lines.map(line => ({ ...line })) } };
  }
}

/** Snapshot of the fields a frame can change, for confirmed-vs-optimistic reconciliation. */
export interface ActionTruthSnapshot {
  aps: number;
  aps_used: number | null;
  statuses: { target: Row[]; caster: Row[] };
  skills: Record<string, unknown>;
}
export function snapshotActionTruth(row: Row): ActionTruthSnapshot {
  const ec = record(row.encounter_creatures) ?? {};
  const statuses = record(row.statuses) ?? {};
  const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value ?? null)) as T;
  return {
    aps: Number(encounterCreatureField(ec, 'aps') ?? 0),
    aps_used: ec.aps_used === undefined ? null : Number(scalar(ec.aps_used)),
    statuses: { target: clone(Array.isArray(statuses.target) ? statuses.target as Row[] : []), caster: clone(Array.isArray(statuses.caster) ? statuses.caster as Row[] : []) },
    skills: clone(record(record(record(row.computed)?.creature)?.skills) ?? {}),
  };
}
export function restoreActionTruth(row: Row, snapshot: ActionTruthSnapshot, rev?: number): void {
  const ec = (row.encounter_creatures ??= {}) as Row;
  applyApsScalar(ec, snapshot.aps, snapshot.aps_used);
  if (Number.isInteger(rev) && rev! >= 0) ec.movement_rev = rev;
  const statuses = (row.statuses ??= {}) as Row;
  statuses.target = snapshot.statuses.target.map(entry => ({ ...entry }));
  statuses.caster = snapshot.statuses.caster.map(entry => ({ ...entry }));
  const computed = (row.computed ??= {}) as Row;
  const creature = (computed.creature ??= {}) as Row;
  creature.skills = JSON.parse(JSON.stringify(snapshot.skills));
}
